import AppRouter from '@/router';
import Client from '@/models/Client';
import { SmsApiDataWithPlatformRegulationsAccepted} from '@/models/LoginData';
import ClientUserApi from '@/modules/client/services/ClientUserApi';
import {TOKEN_GETTER_KEY, TokenType, TOKEN_TYPE_KEY, CLIENT_COOKIE_TOKEN_GETTER, NEW_REAL_ESTATE_PANEL_COOKIE_TOKEN_GETTER} from '@/commons/enums/TokenType';
import Cookies from 'js-cookie';
import processProxy from '@/env/processProxy';
import { shallowRef, ref } from 'vue';
import { Routes } from '@/router/Routes';

class ClientAuthService {
  private readonly CLIENT_TOKEN_KEY: string = TOKEN_GETTER_KEY;

  private clientData = shallowRef<Nullable<Client>>(null);
  private _regulationsAccepted = ref<boolean | null>(null);
  private _ownerRealEstateEnable = ref<boolean | null>(null);
  private _hasValidDeal = ref<boolean | null>(null);

  public get token(): string | null {
    const cookieToken = Cookies.get(CLIENT_COOKIE_TOKEN_GETTER);
    if (cookieToken) {
      Cookies.remove(CLIENT_COOKIE_TOKEN_GETTER);
      this.token = cookieToken;
      return cookieToken;
    } else {
      const localStorageToken = localStorage.getItem(this.CLIENT_TOKEN_KEY);
      return localStorageToken || null;
    }
  }

  public set token(newToken: string | null) {
    if (newToken) {
      localStorage.setItem(TOKEN_TYPE_KEY, TokenType.CLIENT);
      localStorage.setItem(this.CLIENT_TOKEN_KEY, newToken);
    } else {
      console.error('newToken is null');
    }
  }

  public logout() {
    localStorage.removeItem(this.CLIENT_TOKEN_KEY);
    this.User = null;
    this.token = null;
    this.hasAcceptedRegulations = null;
    this.ownerRealEstateEnabled = null;
    this.hasValidDeal = null;
    AppRouter.push({ name: Routes.CLIENT_USER.GUEST_DASHBOARD, query: undefined })
  }

  public get hasAcceptedRegulations(): boolean | null {
    return this._regulationsAccepted.value;
  }

  public set hasAcceptedRegulations(value: boolean | null) {
    this._regulationsAccepted.value = value;
  }

  public get ownerRealEstateEnabled(): boolean | null {
    return this._ownerRealEstateEnable.value;
  }

  public set ownerRealEstateEnabled(value: boolean | null) {
    this._ownerRealEstateEnable.value = value;
  }

  public get needsRestricted(): boolean {
    return !this.hasAcceptedRegulations || this.ownerRealEstateEnabled !== true;
  }

  public get hasValidDeal(): boolean | null {
    return this._hasValidDeal.value
  }
  public set hasValidDeal(value: boolean | null) { 
    this._hasValidDeal.value = value
  }

  public get User(): Nullable<Client> {
    return this.clientData.value;
  }

  public set User(user: Nullable<Client>) {
    this.clientData.value = user;
  }

  public get isTokenValid(): boolean {
    return !!this.token;
  }

  public async getRealEstateAcceessStatusses(): Promise<void> {
    const response = await ClientUserApi.getRealEstateAccessStatus();
    if (response) {
      this.ownerRealEstateEnabled = response.ownerRealEstateEnable;
      this.hasAcceptedRegulations = response.platformRegulationConsent
      this.hasValidDeal = response.hasValidDeal
    }
  }

  async loginWithExchangeCode(exchangeCode: string): Promise<void> {
    try {
    const loginResponse = await ClientUserApi.verifyExchangeCode(exchangeCode);
    this.token = loginResponse;
    await this.fetchUser();
    if (this.hasAcceptedRegulations && this.ownerRealEstateEnabled && !this.hasValidDeal) {
      await ClientUserApi.prepareClientForClientPanel(this.User?.phone!)
      this.hasValidDeal = true;
    }
    const redirect = AppRouter.currentRoute.query.redirect
    if (typeof redirect === 'string') {
       AppRouter.push({path: redirect});
       return;
    }
    if (this.hasAcceptedRegulations) {
      await AppRouter.push({ name: Routes.CLIENT_USER.AUTHENTICATED_CONSULTATION_CONFIRMED });
    } else {
      await AppRouter.push({ name: Routes.CLIENT_USER.RESTRICTED_CONSULTATION_CONFIRMED });
    }  } catch(e) {
    localStorage.removeItem(this.CLIENT_TOKEN_KEY);
    this.User = null;
    this.token = null;
    this.hasAcceptedRegulations = null;
    this.ownerRealEstateEnabled = null;
    this.hasValidDeal = null;
    throw e
    }
  }

  async login(clientLoginData: SmsApiDataWithPlatformRegulationsAccepted): Promise<void> {
    try {
    const loginResponse = await ClientUserApi.checkSms(clientLoginData)
    this.token = loginResponse.authToken;
    await this.fetchUser(); 
    if (this.hasAcceptedRegulations && this.ownerRealEstateEnabled && !this.hasValidDeal) {
      await ClientUserApi.prepareClientForClientPanel(this.User?.phone!)
      this.hasValidDeal = true;
    }
    // Redirect based on regulations status
    const redirect = AppRouter.currentRoute.query.redirect
    if (typeof redirect === 'string') {
       await AppRouter.push({path: redirect});
       return;
    }
    if (this.hasAcceptedRegulations) {
      await AppRouter.push({ name: Routes.CLIENT_USER.CLIENT_DASHBOARD });
    } else {
      await AppRouter.push({ name: Routes.CLIENT_USER.RESTRICTED_DASHBOARD });
    }  } catch(e) {
      if (e.response?.data?.key === 'deals.unable_to_create_deal') {
      await AppRouter.push({ name: Routes.CLIENT_USER.CLIENT_DASHBOARD });
      return;
      }
    localStorage.removeItem(this.CLIENT_TOKEN_KEY);
    this.User = null;
    this.token = null;
    this.hasAcceptedRegulations = null;
    this.ownerRealEstateEnabled = null;
    this.hasValidDeal = null;
    throw e
    }
  }

  public async fetchUser(): Promise<Client> {
    const client = await ClientUserApi.getUser();
    this.User = client;
    await this.getRealEstateAcceessStatusses()
    return client;
  }

  public goToEKO(lastPath: string = ''): void {
    if (lastPath.startsWith('/')) {
      lastPath = lastPath.slice(1, lastPath.length);
    }
    if (this.token) {
      Cookies.set(CLIENT_COOKIE_TOKEN_GETTER, this.token, {
        domain: processProxy.VUE_APP_COOKIE_URL,
        expires: 1 / 288, // 5 minutes
      });
      const ekoPanel = processProxy.VUE_APP_EKO_URL + lastPath;
      if (ekoPanel) {
        window.location.href = ekoPanel;
      } else {
        console.error('Cannot find client panel URL', processProxy.VUE_APP_EKO_URL);
      }
    } else {
      throw new Error('Cannot find valid auth token');
    }
  }

  public goToNewRealEstatePanel(): void {
    if (this.token) {
      Cookies.set(NEW_REAL_ESTATE_PANEL_COOKIE_TOKEN_GETTER, this.token, {
        domain: processProxy.VUE_APP_COOKIE_URL,
        expires: 1, // 24 hours
      });
    }
    const newPanelUrl = processProxy.VUE_APP_NEW_REAL_ESTATE_PANEL_URL;
    if (newPanelUrl) {
      window.location.href = newPanelUrl;
    } else {
      console.error('Cannot find new real estate panel URL', processProxy.VUE_APP_NEW_REAL_ESTATE_PANEL_URL);
    }
  }
}

export default new ClientAuthService();
